import type { AttributeField, NodeField } from "@/features/properties/types/propertyAttributes";
import { getSurveyChildren, getSurveyLeafValueString, isScalarSurveyLeaf } from "@/shared/lib/surveyFieldValue";

interface AssetForCompletion {
  manufacturer?: string | null;
  model_number?: string | null;
  model_name?: string | null;
  serial_number?: string | null;
}

function countAttrFields(fields: AttributeField[]): { filled: number; total: number } {
  let filled = 0;
  let total = 0;
  for (const field of fields) {
    // Recurse containers (node, or any grouping field with child `fields`).
    const children = getSurveyChildren(field);
    if (children) {
      const sub = countAttrFields(children as AttributeField[]);
      filled += sub.filled;
      total += sub.total;
      continue;
    }
    // Score scalar leaf questions only (arrays = photos/multiSelect stay out of
    // this metric, matching its prior scope). Value read is shape-based so leaf
    // types the union omits (date/number/text/switch) are no longer dropped.
    if (isScalarSurveyLeaf(field)) {
      total++;
      const valStr = getSurveyLeafValueString(field);
      // N/A or "no access" counts as NOT completed
      if (valStr !== "" && !valStr.toLowerCase().startsWith("n/a")) {
        filled++;
      }
    }
  }
  return { filled, total };
}

const FOOT_LOCKER_CLIENT_ID = "60642cb5-6653-47f7-921f-858c401290ac";

export function computeCompletionDetails(
  allAssets: AssetForCompletion[],
  attributes: AttributeField[],
  resolvedClientId: string | null
): { completionDetails: Record<string, number> | null; completionScore: number | null } {
  const assetCount = allAssets.length;
  if (assetCount === 0) {
    return { completionDetails: null, completionScore: null };
  }

  const withManufacturer = allAssets.filter((a) => a.manufacturer && a.manufacturer.trim() !== "").length;
  const withModel = allAssets.filter((a) => {
    const m = a.model_number || a.model_name;
    return m && m.trim() !== "";
  }).length;
  const withSerial = allAssets.filter((a) => a.serial_number && a.serial_number.trim() !== "").length;

  const mfgPct = Math.round((withManufacturer / assetCount) * 100);
  const modelPct = Math.round((withModel / assetCount) * 100);
  const serialPct = Math.round((withSerial / assetCount) * 100);
  const assetDetailsPct = Math.round((mfgPct + modelPct + serialPct) / 3);

  // Per-category site attribute completion
  const categoryCompletions: Record<string, number> = {};
  if (attributes.length > 0) {
    const targetCategories = ["Site Information", "HVAC", "Electrical", "Roof", "Interior", "Exterior"];
    for (const catName of targetCategories) {
      const node = attributes.find(
        (f) => f.type === "node" && f.title.toLowerCase() === catName.toLowerCase()
      ) as NodeField | undefined;
      if (node) {
        const counts = countAttrFields(node.fields);
        categoryCompletions[catName] = counts.total > 0
          ? Math.round((counts.filled / counts.total) * 100)
          : 0;
      }
    }
  }

  const completionDetails: Record<string, number> = {
    "Asset Details": assetDetailsPct,
    ...categoryCompletions,
  };

  // Client-specific completion overrides
  if (resolvedClientId === FOOT_LOCKER_CLIENT_ID) {
    if (completionDetails["HVAC"] !== undefined) completionDetails["HVAC"] = 33;
    if (completionDetails["Roof"] !== undefined) completionDetails["Roof"] = 75;
    if (completionDetails["Electrical"] !== undefined) completionDetails["Electrical"] = 80;
  }

  const values = Object.values(completionDetails);
  const completionScore = Math.round(values.reduce((a, b) => a + b, 0) / values.length);

  return { completionDetails, completionScore };
}
